import { FormEvent, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { fetchKonusFormData, submitKonusForm, type KonusFormData } from "@/api/konusForm";
import { Card } from "@/components/Card";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import { ANREDEN_KONUS as ANREDEN } from "@/api/constants";

function formatDe(iso: string): string {
  if (!iso || iso.length < 10) return iso;
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${d}.${m}.${y}`;
}

const label: React.CSSProperties = {
  display: "block",
  fontSize: 11,
  fontWeight: 600,
  color: "var(--fg-muted)",
  marginBottom: 4,
  textTransform: "uppercase",
  letterSpacing: ".04em",
};

const input: React.CSSProperties = {
  width: "100%",
  padding: "8px 10px",
  borderRadius: 6,
  border: "1px solid var(--border)",
  background: "var(--bg-card)",
  color: "var(--fg)",
  fontSize: 13,
  fontFamily: "var(--font-body)",
  boxSizing: "border-box",
};

function Field({
  id,
  labelText,
  type = "text",
  value,
  onChange,
  required,
  options,
}: {
  id: string;
  labelText: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  options?: string[];
}) {
  return (
    <div>
      <label htmlFor={id} style={label}>
        {labelText}{required && " *"}
      </label>
      {options ? (
        <select id={id} value={value} onChange={(e) => onChange(e.target.value)} style={input} required={required}>
          <option value="">— bitte wählen —</option>
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input
          id={id}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={input}
          required={required}
        />
      )}
    </div>
  );
}

function NumberField({
  id,
  labelText,
  value,
  onChange,
}: {
  id: string;
  labelText: string;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <div>
      <label htmlFor={id} style={label}>{labelText}</label>
      <input
        id={id}
        type="number"
        min={0}
        max={999}
        value={value}
        onChange={(e) => onChange(Math.max(0, parseInt(e.target.value) || 0))}
        style={input}
      />
    </div>
  );
}

export function KonusFormPage() {
  useDocumentTitle("Konus-Daten");
  const { token } = useParams<{ token: string }>();
  const [formData, setFormData] = useState<KonusFormData | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Formularfelder
  const [personenAb16, setPersonenAb16] = useState(0);
  const [kinder615, setKinder615] = useState(0);
  const [kinderU6, setKinderU6] = useState(0);
  const [anrede, setAnrede] = useState("");
  const [vorname, setVorname] = useState("");
  const [nachname, setNachname] = useState("");
  const [geburtsdatum, setGeburtsdatum] = useState("");
  const [strasse, setStrasse] = useState("");
  const [hausnummer, setHausnummer] = useState("");
  const [plz, setPlz] = useState("");
  const [ort, setOrt] = useState("");
  const [land, setLand] = useState("DE");

  useEffect(() => {
    if (!token) {
      setLoadError("Kein Token in der URL gefunden.");
      return;
    }
    fetchKonusFormData(token)
      .then((data) => {
        setFormData(data);
        setPersonenAb16(data.personen_ab_16 || 0);
        setKinder615(data.kinder_6_bis_15 || 0);
        setKinderU6(data.kinder_unter_6 || 0);
        setAnrede(data.anrede || "");
        setVorname(data.vorname || "");
        setNachname(data.nachname || "");
        setGeburtsdatum(data.gruppenleiter_geburtsdatum || "");
      })
      .catch((e: Error) => {
        setLoadError(e.message || "Link ungültig oder abgelaufen.");
      });
  }, [token]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!token) return;
    setPending(true);
    setError(null);
    try {
      await submitKonusForm(token, {
        personen_ab_16: personenAb16,
        kinder_6_bis_15: kinder615,
        kinder_unter_6: kinderU6,
        anrede,
        vorname,
        nachname,
        geburtsdatum,
        adresse_strasse: strasse,
        adresse_hausnummer: hausnummer,
        adresse_plz: plz,
        adresse_ort: ort,
        adresse_land: land,
      });
      setSubmitted(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Übermittlung fehlgeschlagen.");
    } finally {
      setPending(false);
    }
  }

  const outer: React.CSSProperties = {
    minHeight: "100vh",
    background: "var(--bg)",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    padding: "32px 16px 48px",
    fontFamily: "var(--font-body)",
  };

  const header: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 20,
  };

  if (loadError) {
    return (
      <div style={outer}>
        <Card style={{ width: "min(560px, 100%)" }}>
          <div style={header}>
            <span className="material-icons-outlined" style={{ fontSize: 28, color: "var(--status-cancel)" }}>error_outline</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: 16 }}>Link ungültig</div>
              <div style={{ fontSize: 13, color: "var(--fg-muted)", marginTop: 2 }}>{loadError}</div>
            </div>
          </div>
          <p style={{ fontSize: 13, color: "var(--fg-muted)" }}>
            Bitte wenden Sie sich an das Pfadfinderzentrum Raumünzach, wenn Sie einen neuen Link benötigen.
          </p>
        </Card>
      </div>
    );
  }

  if (!formData) {
    return (
      <div style={{ ...outer, alignItems: "center" }}>
        <div style={{ color: "var(--fg-muted)", fontSize: 13 }}>Lade…</div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div style={outer}>
        <Card style={{ width: "min(560px, 100%)" }}>
          <div style={header}>
            <span className="material-icons-outlined" style={{ fontSize: 32, color: "var(--status-closed)" }}>check_circle</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: 18 }}>Vielen Dank!</div>
              <div style={{ fontSize: 13, color: "var(--fg-muted)", marginTop: 2 }}>Ihre Meldedaten wurden erfolgreich übermittelt.</div>
            </div>
          </div>
          <div style={{ fontSize: 13, color: "var(--fg-muted)", borderTop: "1px solid var(--border)", paddingTop: 14 }}>
            Die Daten werden vom Pfadfinderzentrum Raumünzach bei der Meldebehörde eingetragen.
            Sie müssen nichts weiter unternehmen.
          </div>
        </Card>
      </div>
    );
  }

  const einheitenText = formData.einheiten.length > 0 ? formData.einheiten.join(", ") : "—";

  return (
    <div style={outer}>
      <div style={{ width: "min(600px, 100%)" }}>
        {/* Header */}
        <div style={{ marginBottom: 20, textAlign: "center" }}>
          <div style={{ fontSize: 13, color: "var(--fg-muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".06em" }}>
            Pfadfinderzentrum Raumünzach
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, fontFamily: "var(--font-display)", marginTop: 4 }}>
            Meldedaten-Erfassung
          </div>
        </div>

        {/* Buchungs-Info */}
        <Card style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--fg-muted)", marginBottom: 10 }}>
            Ihre Buchung
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 16px", fontSize: 13 }}>
            <div><span style={{ color: "var(--fg-muted)" }}>Gruppe: </span><strong>{formData.gruppenname}</strong></div>
            {formData.buchungsreferenz && (
              <div><span style={{ color: "var(--fg-muted)" }}>Ref.: </span><strong>{formData.buchungsreferenz}</strong></div>
            )}
            <div><span style={{ color: "var(--fg-muted)" }}>Anreise: </span>{formatDe(formData.check_in)}</div>
            <div><span style={{ color: "var(--fg-muted)" }}>Abreise: </span>{formatDe(formData.check_out)}</div>
            <div><span style={{ color: "var(--fg-muted)" }}>Einheiten: </span>{einheitenText}</div>
          </div>
        </Card>

        {/* Formular */}
        <Card>
          <div style={{ fontSize: 13, color: "var(--fg-muted)", marginBottom: 18 }}>
            Bitte tragen Sie folgende Daten ein. Sie werden für die gesetzlich vorgeschriebene Gästemeldung beim Einwohnermeldeamt benötigt.
          </div>

          <form onSubmit={onSubmit} style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {/* Personenzahlen */}
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Personenanzahl</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
                <NumberField id="p16" labelText="ab 16 Jahre" value={personenAb16} onChange={setPersonenAb16} />
                <NumberField id="k615" labelText="6–15 Jahre" value={kinder615} onChange={setKinder615} />
                <NumberField id="ku6" labelText="unter 6 Jahre" value={kinderU6} onChange={setKinderU6} />
              </div>
            </div>

            <hr style={{ border: "none", borderTop: "1px solid var(--border)", margin: 0 }} />

            {/* Gruppenleiter */}
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Gruppenleiter/in</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <Field id="anrede" labelText="Anrede" value={anrede} onChange={setAnrede} options={ANREDEN} required />
                <Field id="geburtsdatum" labelText="Geburtsdatum" type="date" value={geburtsdatum} onChange={setGeburtsdatum} required />
                <Field id="vorname" labelText="Vorname" value={vorname} onChange={setVorname} required />
                <Field id="nachname" labelText="Nachname" value={nachname} onChange={setNachname} required />
              </div>
            </div>

            <hr style={{ border: "none", borderTop: "1px solid var(--border)", margin: 0 }} />

            {/* Adresse */}
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Adresse</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 120px", gap: 12 }}>
                  <Field id="strasse" labelText="Straße" value={strasse} onChange={setStrasse} required />
                  <Field id="hausnummer" labelText="Hausnummer" value={hausnummer} onChange={setHausnummer} required />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: 12 }}>
                  <Field id="plz" labelText="PLZ" value={plz} onChange={setPlz} required />
                  <Field id="ort" labelText="Ort" value={ort} onChange={setOrt} required />
                </div>
                <Field id="land" labelText="Land (ISO-Code)" value={land} onChange={setLand} />
              </div>
            </div>

            {error && (
              <div style={{ color: "var(--status-cancel)", fontSize: 13, padding: "8px 12px", background: "color-mix(in oklab, var(--status-cancel) 10%, var(--bg-card))", borderRadius: 6 }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={pending}
              style={{
                padding: "11px 24px",
                background: pending ? "var(--fg-muted)" : "var(--accent)",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 600,
                cursor: pending ? "default" : "pointer",
                fontFamily: "var(--font-body)",
                alignSelf: "flex-end",
              }}
            >
              {pending ? "Wird gesendet…" : "Daten absenden"}
            </button>
          </form>
        </Card>
      </div>
    </div>
  );
}
